/**
 * Comparison Store - AJAX Wrapper for server Comparison System
 *
 * IMPORTANT: This store ONLY handles UI and AJAX calls.
 * All business logic is handled by PHP (lib_comparison.inc.php)
 *
 * Responsibilities:
 * - Badge count (initialized from DOM, updated via AJAX)
 * - Loading states
 * - AJAX toggle calls to PHP endpoint
 * - Item list (read from server response)
 */
export default {
  // Runtime state (initialized from window object set by PHP)
  count: 0,
  items: [],

  // UI state only
  loading: false,
  processingItems: new Set(),

  /** Base path for AJAX URLs — uses server's _env or falls back to '/' */
  get basePath() {
    return window._env?.platform?.path || '/';
  },

  init() {
    // Initialize empty, then fetch from server via AJAX
    this.count = 0;
    this.items = [];

    // Fetch initial data from server
    this.updateCount();
  },

  /**
   * Update comparison count and items from server via AJAX
   */
  async updateCount() {
    try {
      const response = await fetch(this.basePath + 'ajax/comparison.json', {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (response.ok) {
        const data = await response.json();
        this.count = data.count || 0;
        this.items = data.items || [];
      }
    } catch (error) {
      // Silently fail - count will update on next interaction
    }
  },

  /**
   * Toggle comparison item via AJAX
   * @param {number} productId
   * @returns {Promise}
   */
  async toggle(productId) {
    if (this.processingItems.has(productId)) {
      return Promise.resolve();
    }

    this.processingItems.add(productId);
    this.loading = true;
    if (window.Loader) {
      window.Loader.show('comparison-toggle-' + productId, window.tx('loadingUpdating', 'Updating...'));
    }

    try {
      const formData = new FormData();
      formData.append('product_id', productId);
      formData.append('toggle_comparison', 'true');

      const response = await fetch(this.basePath + 'ajax/comparison.json', {
        method: 'POST',
        body: formData,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (data.success) {
        // Update from server response
        this.count = data.count;
        this.items = data.items;
        if (window.Notification) {
          window.Notification.success(window.tx('comparisonUpdated', 'Comparison updated'), 2000);
        }
      } else {
        throw new Error(data.message || 'Failed to toggle comparison');
      }

      return data;
    } catch (error) {
      if (window.Notification) {
        window.Notification.error(window.tx('comparisonError', 'Comparison error'), 3000);
      }
      throw error;
    } finally {
      this.processingItems.delete(productId);
      this.loading = false;
      if (window.Loader) {
        window.Loader.hide('comparison-toggle-' + productId);
      }
    }
  },

  /**
   * Check if product is in comparison
   * @param {number} productId
   * @returns {boolean}
   */
  isInComparison(productId) {
    return this.items.includes(parseInt(productId));
  },

  /**
   * Check if product is being processed
   * @param {number} productId
   * @returns {boolean}
   */
  isProcessing(productId) {
    return this.processingItems.has(productId);
  },
};
